/**
 * Voice transcription client.
 *
 * Companion to `uploadVoiceRecording`: after the audio has been PUT to S3,
 * the widget calls this helper with the presigned GET URL + trace ids, and
 * the BFF (`POST /api/chat/v1/media/transcribe`) proxies through to the n8n
 * transcription webhook. The n8n workflow runs OpenAI Whisper (batch) and
 * returns a single JSON body, so the response here is a one-shot
 * `{ transcript }` rather than a stream.
 *
 *   { "transcript": "What are the admission requirements?" }
 *
 * Errors are translated into a structured `reason` so the parent component
 * can pick the right `WidgetNotice` variant and decide whether to retry
 * only the transcription step or restart from the upload.
 */

export type VoiceTranscribeErrorReason =
  | 'transcribe_failed'      // generic failure (502, unknown 5xx, etc.)
  | 'empty_audio'            // 422 — silent or unintelligible recording
  | 'unsupported_audio'      // 415 — n8n rejected the audio bytes
  | 'timeout'                // 504 — upstream transcription timed out
  | 'not_configured'         // 503 — feature not available on this env
  | 'invalid_request'        // 400 — should not happen if uploader is correct
  | 'network_error';         // fetch threw before/after we got a status

export class VoiceTranscribeError extends Error {
  reason: VoiceTranscribeErrorReason;
  status?: number;

  constructor(reason: VoiceTranscribeErrorReason, message: string, status?: number) {
    super(message);
    this.name = 'VoiceTranscribeError';
    this.reason = reason;
    this.status = status;
  }
}

export interface VoiceTranscribeResult {
  /** Final transcript, with surrounding whitespace trimmed. */
  transcript: string;
}

export interface TranscribeVoiceRecordingOptions {
  /** Origin of the BFF (e.g. https://chat.example.com). */
  apiEndpoint: string;
  /** Presigned S3 GET URL minted by `/api/chat/v1/media/upload-url`. */
  audioUrl: string;
  /** S3 object key returned alongside the presigned URLs. */
  mediaKey: string;
  /** Frontend-generated message id. Same id is later used for the chat message. */
  messageId: string;
  /** Conversation thread id. */
  threadId: string;
  /** Visitor session id. */
  sessionId: string;
  /** Institution id. */
  institutionId: string;
  /** Optional fetch override for testing. */
  fetchImpl?: typeof fetch;
}

/**
 * Map the BFF response status to a `VoiceTranscribeErrorReason`. Status
 * takes precedence; if it doesn't match a known mapping we fall back to
 * the message body's wording.
 */
function classifyFailure(status: number, errorBody: string): VoiceTranscribeErrorReason {
  if (status === 422) return 'empty_audio';
  if (status === 415) return 'unsupported_audio';
  if (status === 504) return 'timeout';
  if (status === 503) return 'not_configured';
  if (status === 400) return 'invalid_request';
  const lower = errorBody.toLowerCase();
  if (lower.includes('empty')) return 'empty_audio';
  if (lower.includes('content type') || lower.includes('unsupported')) return 'unsupported_audio';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'timeout';
  return 'transcribe_failed';
}

/**
 * Read the BFF error body from a non-2xx response. Routes return
 * `{ type: 'error', error: <message> }` JSON via apiErrorResponse,
 * but we tolerate plain text too.
 */
async function readErrorBody(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return '';
    try {
      const parsed = JSON.parse(text) as { error?: string; content?: string };
      return parsed.error ?? parsed.content ?? text;
    } catch {
      return text;
    }
  } catch {
    return '';
  }
}

/**
 * POSTs the presigned GET URL + trace ids to the BFF transcribe endpoint
 * and returns the resolved transcript. Throws `VoiceTranscribeError` with
 * a structured `reason` on any failure path.
 */
export async function transcribeVoiceRecording(
  options: TranscribeVoiceRecordingOptions,
): Promise<VoiceTranscribeResult> {
  const doFetch = options.fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await doFetch(`${options.apiEndpoint}/api/chat/v1/media/transcribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        audioUrl: options.audioUrl,
        mediaKey: options.mediaKey,
        messageId: options.messageId,
        threadId: options.threadId,
        sessionId: options.sessionId,
        institutionId: options.institutionId,
      }),
    });
  } catch (err) {
    throw new VoiceTranscribeError(
      'network_error',
      err instanceof Error ? err.message : 'Network error while requesting transcription.',
    );
  }

  if (!response.ok) {
    const errorBody = await readErrorBody(response);
    const reason = classifyFailure(response.status, errorBody);
    throw new VoiceTranscribeError(
      reason,
      errorBody || `Transcription failed (status ${response.status}).`,
      response.status,
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (err) {
    throw new VoiceTranscribeError(
      'transcribe_failed',
      err instanceof Error ? err.message : 'Transcription response was not valid JSON.',
      response.status,
    );
  }

  const transcript =
    typeof body === 'object' && body !== null && typeof (body as { transcript?: unknown }).transcript === 'string'
      ? (body as { transcript: string }).transcript.trim()
      : '';

  if (!transcript) {
    throw new VoiceTranscribeError(
      'empty_audio',
      'Transcription returned an empty transcript.',
      response.status,
    );
  }

  return { transcript };
}
